import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {FaHome,FaInfoCircle,FaUser,FaPhoneAlt,FaGraduationCap,} from "react-icons/fa";

function FloatingMenu() {
  const navigate = useNavigate();

  const SIZE = 56;

  const [open, setOpen] = useState(false);
  const [showBtn, setShowBtn] = useState(true);

  const [position, setPosition] = useState({
    x: window.innerWidth - 75,
    y: window.innerHeight - 170,
  });

  const [dragging, setDragging] = useState(false);

  const dragRef = useRef({
    offsetX: 0,
    offsetY: 0,
  });

  const movedRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });

  /* ================= START DRAG ================= */
  const startDrag = (e) => {
    if (open) return;

    e.preventDefault();

    const clientX = e.touches?.[0]?.clientX || e.clientX;
    const clientY = e.touches?.[0]?.clientY || e.clientY;

    startRef.current = { x: clientX, y: clientY };
    movedRef.current = false;

    dragRef.current = {
      offsetX: clientX - position.x,
      offsetY: clientY - position.y,
    };

    setDragging(true);
  };

  /* ================= MOVE ================= */
  const moveDrag = (e) => {
    if (!dragging) return;

    if (e.cancelable) e.preventDefault();

    const clientX = e.touches?.[0]?.clientX || e.clientX;
    const clientY = e.touches?.[0]?.clientY || e.clientY;

    // movement detection
    const dx = Math.abs(clientX - startRef.current.x);
    const dy = Math.abs(clientY - startRef.current.y);
    if (dx > 5 || dy > 5) movedRef.current = true;

    let newX = clientX - dragRef.current.offsetX;
    let newY = clientY - dragRef.current.offsetY;

    const maxX = window.innerWidth - SIZE - 6;
    const maxY = window.innerHeight - SIZE - 6;

    if (newX < 6) newX = 6;
    if (newY < 6) newY = 6;
    if (newX > maxX) newX = maxX;
    if (newY > maxY) newY = maxY;

    setPosition({
      x: newX,
      y: newY,
    });
  };

  /* ================= END DRAG ================= */
  const stopDrag = () => {
    if (!dragging) return;

    setDragging(false);

    const side =
      position.x < window.innerWidth / 2
        ? 8
        : window.innerWidth - SIZE - 8;

    setPosition({
      x: side,
      y: position.y,
    });

    setTimeout(() => {
      movedRef.current = false;
    }, 50);
  };

  /* ================= EVENTS ================= */
  useEffect(() => {
    window.addEventListener("mousemove", moveDrag);
    window.addEventListener("mouseup", stopDrag);

    window.addEventListener("touchmove", moveDrag, { passive: false });
    window.addEventListener("touchend", stopDrag);

    return () => {
      window.removeEventListener("mousemove", moveDrag);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchmove", moveDrag);
      window.removeEventListener("touchend", stopDrag);
    };
  }, [dragging, position]);

  /* ================= OPEN MENU ================= */
  const openMenu = () => {
    if (dragging || movedRef.current) return;

    setOpen(true);
    setShowBtn(false);
  };

  const closeMenu = () => {
    setOpen(false);
    setTimeout(() => setShowBtn(true), 180);
  };

  const goPage = (url) => {
    closeMenu();
    navigate(url);
  };

  return (
    <>
      {/* BUTTON */}
      {showBtn && (
        <div
          className="assistive-btn"
          style={{
            left: position.x,
            top: position.y,
            cursor: dragging ? "grabbing" : "grab",
          }}
          onMouseDown={startDrag}
          onTouchStart={startDrag}
          onClick={openMenu}
        >
          <div className="assistive-dot"></div>
        </div>
      )}

      {/* MENU */}
      {open && (
        <>
          <div className="assistive-overlay" onClick={closeMenu}></div>

          <div className="assistive-menu">
            <button className="pos-top" onClick={() => goPage("/about")}>
              <FaInfoCircle />
              <small>About</small>
            </button>

            <button className="pos-left" onClick={() => goPage("/")}>
              <FaHome />
              <small>Home</small>
            </button>

            <button className="pos-right" onClick={() => goPage("/profile")}>
              <FaUser />
              <small>Profile</small>
            </button>

            <button className="pos-bottom" onClick={() => goPage("/contact")}>
              <FaPhoneAlt />
              <small>Contact</small>
            </button>

            <button
              className="center-btn"
              onClick={() => goPage("/select-grade")}
            >
              <FaGraduationCap />
              <small>MCQ</small>
            </button>
          </div>
        </>
      )}

      {/* CSS */}
      <style>
        {`
        .assistive-btn{
          position:fixed;
          width:${SIZE}px;
          height:${SIZE}px;
          border-radius:16px;
          z-index:9999;

          background:rgba(160,160,160,.32);
          backdrop-filter:blur(16px);
          -webkit-backdrop-filter:blur(16px);

          border:1px solid rgba(255,255,255,.18);

          box-shadow:
          inset 0 1px 1px rgba(255,255,255,.16),
          0 8px 20px rgba(0,0,0,.25);

          display:flex;
          align-items:center;
          justify-content:center;

          user-select:none;
          touch-action:none;

          transition:left 0.25s ease, top 0.25s ease;
        }

        .assistive-dot{
          width:14px;
          height:14px;
          border-radius:50%;
          background:#fff;
          opacity:.95;

          pointer-events:none; /* 🔥 FIX */
        }

        .assistive-overlay{
          position:fixed;
          inset:0;
          z-index:9997;
        }

        .assistive-menu{
          position:fixed;
          left:50%;
          top:50%;
          transform:translate(-50%,-50%);
          width:255px;
          height:255px;
          border-radius:34px;
          z-index:9998;

          background:rgba(22,22,22,.96);
          backdrop-filter:blur(18px);
          -webkit-backdrop-filter:blur(18px);

          box-shadow:
          0 18px 45px rgba(0,0,0,.42);

          animation:popIn .22s ease;
        }

        .assistive-menu button{
          position:absolute;
          border:none;
          background:transparent;
          color:#fff;
          cursor:pointer;

          display:flex;
          flex-direction:column;
          align-items:center;
          gap:6px;
        }

        .assistive-menu svg{
          font-size:28px;
        }

        .assistive-menu small{
          font-size:14px;
        }

        .pos-top{
          left:50%;
          top:18px;
          transform:translateX(-50%);
        }

        .pos-left{
          left:18px;
          top:50%;
          transform:translateY(-50%);
        }

        .pos-right{
          right:18px;
          top:50%;
          transform:translateY(-50%);
        }

        .pos-bottom{
          left:50%;
          bottom:18px;
          transform:translateX(-50%);
        }

        .center-btn{
          left:50%;
          top:50%;
          transform:translate(-50%,-50%);
          width:74px;
          height:74px;
          border-radius:50%;
          background:rgba(255,255,255,.08);
        }

        .center-btn svg{
          font-size:30px;
        }

        @keyframes popIn{
          from{
            opacity:0;
            transform:translate(-50%,-50%) scale(.82);
          }
          to{
            opacity:1;
            transform:translate(-50%,-50%) scale(1);
          }
        }

        @media(max-width:768px){
          .assistive-menu{
            width:235px;
            height:235px;
          }

          .assistive-menu svg{
            font-size:25px;
          }

          .assistive-menu small{
            font-size:13px;
          }

          .center-btn{
            width:68px;
            height:68px;
          }
        }
        `}
      </style>
    </>
  );
}

export default FloatingMenu;